/* Auto Fix Auto Electricians - the assistant calls them back. TEMPLATE-OWNED; the card's copy
 * is outbound-scenario.js, yours.
 *
 * The inbound page puts the visitor in the caller's chair. This one puts them in BOTH
 * chairs, in order, and the order is the whole demo:
 *
 *   1. they are the owner. A missed call, a summary, a reply. They tap "Assistant calls
 *      them" and the card holds a loader.
 *   2. they are the customer. The handset beside them rings, they answer, and the
 *      assistant is already speaking.
 *
 * WHY THE LOADER AND THE RING ARE TWO DIFFERENT THINGS. A cold Pipecat Cloud instance
 * takes about ten seconds to boot, and over Daily the bot joins the room BEFORE it can
 * speak. If the handset rang on connect, the visitor would answer into ten seconds of
 * silence - which is the single worst thing this page could do, because on a real phone
 * call that silence is the product failing. So the wait is spent on the OWNER's side,
 * where waiting for a call to go out is ordinary, and the handset only rings once
 * `client.connect()` has resolved - which happens on bot-ready, after the pipeline is up.
 * Answering then is instant, every time, warm or cold.
 *
 * The protocol:
 *   GET  /api/config                      -> {passcodeRequired, maxCallSeconds, outbound}
 *   POST /api/session {passcode}          -> {token}
 *   POST /api/dial {token, mode, brief}   -> web:   {dailyRoom, dailyToken, sessionId}
 *                                            phone: {callSid}
 *   GET  /api/dial/{callSid}              -> {status, answeredBy}   (local server only)
 *   client.sendClientMessage("answer")    -- the bot speaks its opening
 *   client.sendClientMessage("decline")   -- the bot ends the session
 *
 * Read call-kit.js's audio note before touching anything that plays sound.
 */

import { PipecatClient } from "@pipecat-ai/client-js";
import { DailyTransport } from "@pipecat-ai/daily-transport";

import {
  accentColour,
  clock,
  createCaptions,
  createNotes,
  createRinger,
  createToken,
  openAudioContext,
  postJson,
  reportAudioConstraints,
  startWave,
  tap,
} from "./call-kit.js";
import { renderPitch } from "./readout-kit.js";
import { PITCH, SAMPLE, SITUATIONS } from "./outbound-scenario.js";

(() => {
  "use strict";

  const $ = (id) => document.getElementById(id);

  const el = {
    screens: {
      gate: $("screen-gate"),
      idle: $("screen-idle"),
      ringing: $("screen-ringing"),
      call: $("screen-call"),
      ended: $("screen-ended"),
    },
    phoneScreen: $("phone-screen"),
    islandTimer: $("island-timer"),
    gateForm: $("gate-form"),
    passcode: $("passcode"),
    gateError: $("gate-error"),

    briefNumber: $("brief-number"),
    briefName: $("brief-name"),
    briefSummary: $("brief-summary"),
    situationChips: $("situation-chips"),
    replyChips: $("reply-chips"),
    replyText: $("reply-text"),
    dialWeb: $("dial-web"),
    phoneMode: $("phone-mode"),
    dialTo: $("dial-to"),
    dialPhone: $("dial-phone"),
    dialError: $("dial-error"),
    dialStatus: $("dial-status"),
    outboundPitch: $("outbound-pitch"),
    outboundPitchTitle: $("outbound-pitch-title"),

    phoneLeadSub: $("phone-lead-sub"),
    idleName: $("idle-name"),

    answerButton: $("answer-button"),
    declineButton: $("decline-button"),
    liveDisc: $("live-disc"),
    wave: $("wave"),
    callState: $("call-state"),
    callTimer: $("call-timer"),
    speakingLabel: $("speaking-label"),
    captions: $("captions"),
    ringNote: $("ring-note"),
    muteButton: $("mute-button"),
    muteLabel: $("mute-label"),
    endButton: $("end-button"),
    limitNote: $("limit-note"),
    endedEyebrow: $("ended-eyebrow"),
    endedTitle: $("ended-title"),
    endedSub: $("ended-sub"),
    remoteAudio: $("remote-audio"),
  };

  let config = { passcodeRequired: false, maxCallSeconds: 300, outbound: { web: true, phone: false } };

  /* THE TOKEN IS MINTED WHEN THE CALL IS PLACED, not when the page loads. This page is read
   * and fiddled with - a card to fill in, a reply to pick - long before anything is dialled,
   * and a token minted on the way in has lapsed by then. See createToken() in call-kit.js.
   *
   * `passcode` is the code the visitor typed at the gate and the server accepted, held here
   * for as long as the tab is open and nowhere else, so that every re-mint presents it to
   * /api/session again. */
  let passcode = "";
  const sessionToken = createToken(() => postJson("/api/session", { passcode }));

  // Live call. Held at module scope so nothing here is garbage collected mid-call —
  // in particular remoteSource, which is what keeps the WebAudio graph alive.
  let client = null;
  let audioCtx = null;
  let remoteSource = null;
  let remoteAnalyser = null;
  let micSource = null;
  let micAnalyser = null;
  let timerTimer = null;
  let stopWave = null;
  let pollTimer = null;
  let answerTimer = null;
  let returnTimer = null;
  let startedAt = 0;
  let dialT0 = 0;
  let ended = false;
  let dialling = false;

  // Which situation is on the card, and therefore which replies are on offer. Read by the
  // reply box's input handler, which has to know what it is comparing typing against.
  let situation = SITUATIONS[0] || { replies: [] };

  const ringer = createRinger();
  const waveSources = () => [remoteAnalyser, micAnalyser];
  const caption = createCaptions(el.captions);
  let callerLive = null;
  let agentLive = null;
  let agentBuffer = "";

  /* ── screens ────────────────────────────────────────────────────── */

  function show(name) {
    for (const [key, node] of Object.entries(el.screens)) node.hidden = key !== name;
  }

  function fail(node, message) {
    node.textContent = message;
    node.hidden = false;
  }

  /* ── the card ───────────────────────────────────────────────────────
   * Filled from outbound-scenario.js and then owned by whoever is reading it: every
   * field is contenteditable, so `readBrief()` is the only source of truth about what
   * will actually be said. Nothing is mirrored into a variable on the way past. */

  function renderCard() {
    el.briefNumber.textContent = SAMPLE.number;
    el.briefName.textContent = SAMPLE.name;
    el.briefName.dataset.placeholder = "Who you're ringing";
    el.briefSummary.dataset.placeholder = "Why they rang, and anything else it may need";
    el.replyText.dataset.placeholder = "Type what you'd tell them";

    el.situationChips.replaceChildren(...SITUATIONS.map((entry) =>
      chip(entry, "situation-chip", () => pickSituation(entry))
    ));
    pickSituation(situation);
    reflectName();
  }

  /** One chip in one of the two rows. The rows differ in what choosing does, not in this. */
  function chip(entry, className, onPick) {
    const item = document.createElement("li");
    const button = document.createElement("button");
    button.type = "button";
    button.className = className;
    button.dataset.tone = entry.tone || "custom";
    button.dataset.id = entry.id;
    button.setAttribute("aria-pressed", "false");
    button.textContent = entry.label;
    button.addEventListener("click", onPick);
    item.appendChild(button);
    return item;
  }

  function press(row, selector, id) {
    for (const button of row.querySelectorAll(selector)) {
      button.setAttribute("aria-pressed", String(button.dataset.id === id));
    }
  }

  /** Swap what they rang about: the note, and the replies that answer it.
   *
   * THE REPLIES GO WITH IT, and that is the whole reason this is one control rather than
   * two. "Yes, it's ready" under a note about an unreturned message is an answer the
   * assistant would deliver, in whatever words, to somebody it makes no sense to. */
  function pickSituation(entry) {
    situation = entry;
    press(el.situationChips, ".situation-chip", entry.id);
    el.briefSummary.textContent = entry.summary || "";

    const replies = entry.replies || [];
    el.replyChips.replaceChildren(...replies.map((reply) =>
      chip(reply, "reply-chip", () => pickReply(reply))
    ));
    if (replies.length) pickReply(replies[0], { focus: false });
    else el.replyText.textContent = "";
  }

  /** Whose phone that is, said beside the phone. Follows the card's name as it is edited.
   *
   * The column used to explain itself on the device's own screen, which is the last place
   * anybody reads - and the question it has to answer ("whose phone?") is the one that has
   * to land before the button is worth pressing. */
  function reflectName() {
    const name = (el.briefName.textContent || "").trim();
    el.idleName.textContent = name || "Their phone";
    el.phoneLeadSub.textContent = name
      ? `What ${name} sees when your assistant rings.`
      : "What they see when your assistant rings.";
  }

  function pickReply(reply, { focus = true } = {}) {
    press(el.replyChips, ".reply-chip", reply.id);
    el.replyText.textContent = reply.text || "";
    // "Something else" is a chip that hands the box over rather than filling it, so it
    // puts the cursor where the owner is about to type - unless it arrived by being the
    // first reply of a freshly picked situation, where stealing focus would yank the page.
    if (reply.custom && focus) el.replyText.focus();
  }

  /** What is on the card right now, as the brief the API takes.
   *
   * NO `question`. `OutboundBrief` still carries one - a dashboard button that already has
   * the inbound call's headline should send it, and the SDK reads it - but this card asks
   * for a name, a note and a reply, and a fourth box that restated the note was the one
   * thing on the page nobody could tell apart from its neighbour. */
  function readBrief() {
    const text = (node) => (node.textContent || "").trim();
    return {
      contact_name: text(el.briefName),
      summary: text(el.briefSummary),
      reply: text(el.replyText),
    };
  }

  function lockCard(locked) {
    dialling = locked;
    el.dialWeb.disabled = locked;
    if (el.dialPhone) el.dialPhone.disabled = locked;
    for (const button of el.replyChips.querySelectorAll(".reply-chip")) button.disabled = locked;
    for (const button of el.situationChips.querySelectorAll(".situation-chip")) {
      button.disabled = locked;
    }
    for (const field of [el.briefName, el.briefSummary, el.replyText]) {
      field.contentEditable = locked ? "false" : "plaintext-only";
    }
  }

  /* The owner-side script, and the reason the visitor is not staring at a spinner. It
   * names the steps a cold boot actually takes, in the order the pod takes them, so the
   * length of the wait is legible rather than mysterious. A warm instance is through
   * before the second line fires and shows none of it. */
  const DIAL_NOTES = [
    [0, "Waking your assistant…"],
    [4, "Reading the brief…"],
    [9, "Getting its voice ready…"],
    [14, "Dialling…"],
    [26, "Taking longer than usual. Hang on."],
    [45, "Still not through. Close this and try again."],
  ];
  const dialNotes = createNotes(el.dialStatus, DIAL_NOTES);

  /* ── audio ──────────────────────────────────────────────────────────
   * The bot's voice MUST be played through the <audio> element. That is the path the
   * browser's AEC uses as its reference signal when it subtracts what the speakers are
   * playing from what the microphone hears. Route it through audioCtx.destination
   * instead and Chrome's AEC flatlines: the agent hears itself, transcribes itself, and
   * interrupts itself.
   *
   * `tap()` (call-kit.js) takes the analysers off both tracks and connects them to
   * nothing, so the waveform costs no second playback path and no second microphone. */

  function attachBotAudio(track) {
    const stream = new MediaStream([track]);
    el.remoteAudio.srcObject = stream;
    el.remoteAudio.play().catch(() => {});

    const tapped = tap(audioCtx, track);
    if (tapped) ({ source: remoteSource, analyser: remoteAnalyser } = tapped);
  }

  function attachMicAnalyser(track) {
    if (micAnalyser) return;
    const tapped = tap(audioCtx, track);
    if (tapped) ({ source: micSource, analyser: micAnalyser } = tapped);
  }

  /* ── the client ─────────────────────────────────────────────────────
   * The inbound page's callbacks, minus the read-out (there is no funnel panel here) and
   * minus onBotReady meaning "connected" - here bot-ready means READY TO RING, which is
   * an entirely different moment and is handled where connect() resolves. */

  function buildClient() {
    return new PipecatClient({
      transport: new DailyTransport({
        inputSettings: {
          audio: {
            settings: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
          },
        },
      }),
      // THE MICROPHONE STAYS SHUT UNTIL THEY ANSWER. The bot is in the room for the whole
      // of the ring, and a live microphone through it would put the room's own noise -
      // and the ringtone - into the agent's ears before the call has started.
      enableMic: false,
      enableCam: false,
      callbacks: {
        onUserStartedSpeaking: () => {
          el.liveDisc.classList.add("is-caller");
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "Listening";
        },
        onUserStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "On the call";
        },

        onBotStartedSpeaking: () => {
          el.liveDisc.classList.add("is-agent");
          el.liveDisc.classList.remove("is-caller");
          el.speakingLabel.textContent = "Speaking";
          agentBuffer = "";
          agentLive = caption("Auto Fix Auto Electricians", "caption-agent");
        },
        onBotStoppedSpeaking: () => {
          el.liveDisc.classList.remove("is-agent");
          el.speakingLabel.textContent = "On the call";
          agentLive = null;
        },

        onBotTtsText: (data) => {
          if (agentLive && data && data.text) {
            agentBuffer += (agentBuffer ? " " : "") + data.text;
            agentLive.textContent = agentBuffer;
            el.captions.scrollTop = el.captions.scrollHeight;
          }
        },
        onBotOutput: (data) => {
          if (agentLive && !agentBuffer && data && data.text) agentLive.textContent = data.text;
        },

        onUserTranscript: (data) => {
          if (!data || !data.text) return;
          if (!callerLive) callerLive = caption("You", "caption-caller");
          callerLive.textContent = data.text;
          el.captions.scrollTop = el.captions.scrollHeight;
          if (data.final) callerLive = null;
        },

        // The bot says it is ringing on the same channel the read-out uses. The page does
        // not wait for it - connect() resolving is the signal it rings on - so this is a
        // log line and a cross-check, not a dependency.
        onServerMessage: (data) => {
          if (data && data.type === "outbound-ringing") console.info("the assistant is ready");
        },

        onTrackStarted: (track, participant) => {
          if (track.kind !== "audio") return;
          if (participant && participant.local) attachMicAnalyser(track);
          else attachBotAudio(track);
        },

        onDeviceError: (error) => {
          console.warn("device error", error);
          hangUp("This needs your microphone. Allow access and try again.");
        },

        onBotDisconnected: () => hangUp(null),
        onDisconnected: () => hangUp(null),
        onError: (message) => console.warn("agent error", message),
        onMessageError: (message) => console.warn("agent message error", message),
      },
    });
  }

  /* ── the web flow ───────────────────────────────────────────────── */

  async function dialWeb() {
    if (dialling) return;
    const brief = readBrief();
    if (!brief.contact_name) {
      return fail(el.dialError, "Who is the assistant ringing? Add a name to the card.");
    }
    if (!brief.reply) {
      return fail(el.dialError, "Pick a reply, or write one, before sending it.");
    }
    if (config.passcodeRequired && !passcode) {
      show("gate");
      el.passcode.focus();
      return;
    }

    el.dialError.hidden = true;
    // BEFORE THE FIRST AWAIT, while this tap still counts (rule ONE in call-kit.js). The
    // play() has no source yet; what it buys is the element's leave to play the bot's
    // track when that arrives. A context left open by a failed mint is reused, not leaked.
    if (!audioCtx) audioCtx = openAudioContext();
    el.remoteAudio.play().catch(() => {});

    // MINTED HERE, a moment before it is spent, not when the page loaded.
    const minted = await sessionToken.get();
    if (!minted.ok) return mintFailed(minted);

    ended = false;
    lockCard(true);
    clearTimeout(returnTimer);
    returnTimer = null;
    show("idle");
    el.captions.replaceChildren();
    startedAt = 0;
    dialT0 = performance.now();
    dialNotes.schedule(dialT0);

    let session;
    try {
      let res = await postJson("/api/dial", { token: minted.token, mode: "web", brief });
      if (res.status === 401) {
        // The server refused a token this page thought was live: a clock further behind
        // than the margin, or a secret rotated under us. One re-mint and one retry, because
        // nobody should pay a reload for either. A second 401 is a real refusal.
        sessionToken.clear();
        const again = await sessionToken.get();
        if (!again.ok) return mintFailed(again);
        res = await postJson("/api/dial", { token: again.token, mode: "web", brief });
      }
      if (res.status === 503) return dialFailed("The demo line is busy. Try again shortly.");
      if (res.status === 401) return dialFailed("Couldn't start the call. Try again.");
      if (res.status === 422) {
        const body = await res.json().catch(() => ({}));
        return dialFailed(body.detail || "The card is missing something.");
      }
      if (!res.ok) return dialFailed("Couldn't get through to Auto Fix.");
      // `mode` is this route's own, not Pipecat Cloud's, and DailyTransport logs a warning
      // for every key it does not recognise. Everything else goes in unreshaped, exactly
      // as it does on the inbound page.
      const { mode, ...rest } = await res.json();
      session = rest;
    } catch (err) {
      console.error(err);
      return dialFailed("Couldn't get through to Auto Fix.");
    }

    client = buildClient();
    try {
      // RESOLVES ON BOT-READY, which is the whole design: the pipeline is up and the
      // agent can speak the instant it is answered. Only now does the handset ring.
      await client.connect(session);
    } catch (err) {
      console.error(err);
      return dialFailed("Couldn't get through to Auto Fix.");
    }
    startRinging(brief.contact_name);
  }

  function startRinging(name) {
    dialNotes.clear();
    el.dialStatus.hidden = false;
    el.dialStatus.textContent = `Ringing ${name}'s phone…`;
    el.dialStatus.classList.add("is-done");

    show("ringing");
    ringer.start(audioCtx, "incoming");

    // Answering during the first ring cycle would be answering a phone that has not
    // finished making its first sound. The button is unavailable for exactly that long,
    // which also gives the visitor a beat to notice the phone is ringing at all.
    el.answerButton.disabled = true;
    el.declineButton.disabled = true;
    answerTimer = setTimeout(() => {
      el.answerButton.disabled = false;
      el.declineButton.disabled = false;
    }, 2200);
  }

  function dialFailed(message) {
    dialNotes.clear();
    el.dialStatus.hidden = true;
    lockCard(false);
    fail(el.dialError, message);
    teardown();
    show("idle");
  }

  /** What a mint that failed means. /api/session refuses nothing but the passcode. */
  function mintFailed({ status }) {
    if (status === 401 && config.passcodeRequired) {
      // The code was right when the visitor typed it; DEMO_PASSCODE has changed since.
      passcode = "";
      dialNotes.clear();
      el.dialStatus.hidden = true;
      lockCard(false);
      teardown();
      show("gate");
      fail(el.gateError, "That passcode is not right any more. Check the code you were sent.");
      el.passcode.value = "";
      el.passcode.focus();
      return;
    }
    dialFailed("Could not reach the demo server. Try again.");
  }

  async function answer() {
    if (!client) return;
    clearTimeout(answerTimer);
    ringer.stop();
    el.dialStatus.textContent = "On the call.";
    show("call");
    el.liveDisc.classList.remove("is-ringing");
    el.liveDisc.classList.add("is-connected");
    el.callState.hidden = true;
    el.callTimer.hidden = false;
    el.phoneScreen.classList.add("is-live");
    el.speakingLabel.textContent = "on the call";

    // Answer is a tap of its own, the last one before the agent speaks. Unlocking again
    // here covers a browser that let the dial tap's leave lapse during the ring.
    if (audioCtx) audioCtx.resume().catch(() => {});
    el.remoteAudio.play().catch(() => {});

    // In this order, and the order matters: the microphone has to be live before the
    // agent starts talking, or the first thing they say back is lost.
    client.enableMic(true);
    reportAudioConstraints(client);
    // Guarded because it throws on a transport that is not ready, and a throw here would
    // leave the page showing a live call screen with nothing on the other end. The bot
    // only ever rings once `connect()` has resolved, so this is the case where the
    // connection dropped between the ring and the tap.
    try {
      client.sendClientMessage("answer");
    } catch (err) {
      console.warn(err);
      return hangUp("Couldn't get through to Auto Fix.");
    }

    startedAt = Date.now();
    stopWave = startWave(el.wave, accentColour("#ff6b6b"), waveSources);
    timerTimer = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      const reading = clock(secs);
      el.callTimer.textContent = reading;
      el.islandTimer.textContent = reading;
      if (config.maxCallSeconds > 0) {
        const left = config.maxCallSeconds - secs;
        if (left <= 60 && left > 0) {
          el.limitNote.hidden = false;
          el.limitNote.textContent = `This demo call ends in ${left}s.`;
        }
      }
    }, 500);
  }

  function decline() {
    if (!client) return hangUp("Declined.");
    clearTimeout(answerTimer);
    ringer.stop();
    // The bot ends the session itself, so the record says "declined" rather than
    // "disconnected" - which is the difference between a call that was refused and one
    // that fell over. If the transport is already gone, the hangup below is the whole of
    // what is left to do and the distinction is moot.
    try {
      client.sendClientMessage("decline");
    } catch (err) {
      console.warn(err);
    }
    hangUp("Declined.");
  }

  /* ── the phone flow ─────────────────────────────────────────────────
   * A real number, over Twilio. There is nothing to join here - the conversation happens
   * on somebody's actual phone - so the page's job ends at saying what the call is doing.
   * The poll is the LOCAL server's; the hosted deployment has nowhere to keep call status
   * and answers 404, which is treated as no news rather than as an error. */

  const PHONE_STATUS = {
    queued: "Dialling…",
    initiated: "Dialling…",
    ringing: "Ringing now…",
    "in-progress": "They answered. The assistant is talking to them.",
    completed: "Call finished.",
    busy: "Their line was busy.",
    "no-answer": "No answer.",
    failed: "The call could not be placed.",
    machine: "It went to voicemail, so the assistant hung up.",
  };

  async function dialPhone() {
    if (dialling) return;
    const brief = readBrief();
    const to = el.dialTo.value.trim();
    if (!to) return fail(el.dialError, "Which number should it ring?");
    // This route rings somebody's actual phone and fails closed on the passcode, so the
    // gate comes first and the token is minted here, freshly, like every other call.
    if (config.passcodeRequired && !passcode) {
      show("gate");
      el.passcode.focus();
      return;
    }

    el.dialError.hidden = true;
    const minted = await sessionToken.get();
    if (!minted.ok) return mintFailed(minted);

    lockCard(true);
    el.dialStatus.hidden = false;
    el.dialStatus.textContent = `Dialling ${to}…`;

    let body;
    try {
      const res = await postJson("/api/dial", { token: minted.token, mode: "phone", to, brief });
      body = await res.json().catch(() => ({}));
      if (!res.ok) {
        lockCard(false);
        el.dialStatus.hidden = true;
        return fail(el.dialError, body.detail || "Couldn't get through to Auto Fix.");
      }
    } catch (err) {
      console.error(err);
      lockCard(false);
      el.dialStatus.hidden = true;
      return fail(el.dialError, "Couldn't get through to Auto Fix.");
    }

    el.dialStatus.textContent = `Ringing ${to} now…`;
    pollCall(body.callSid);
  }

  function pollCall(callSid) {
    clearInterval(pollTimer);
    if (!callSid) return;
    pollTimer = setInterval(async () => {
      let entry;
      try {
        const res = await fetch(`/api/dial/${encodeURIComponent(callSid)}`);
        if (!res.ok) return;              // 404 on the hosted demo: no news, not an error
        entry = await res.json();
      } catch (_) {
        return;
      }
      el.dialStatus.textContent = PHONE_STATUS[entry.status] || `Call ${entry.status}.`;
      if (["completed", "busy", "no-answer", "failed", "machine"].includes(entry.status)) {
        clearInterval(pollTimer);
        pollTimer = null;
        el.dialStatus.classList.add("is-done");
        lockCard(false);
      }
    }, 2000);
  }

  /* ── ending ─────────────────────────────────────────────────────── */

  function teardown() {
    clearInterval(timerTimer);
    clearTimeout(answerTimer);
    clearInterval(pollTimer);
    timerTimer = answerTimer = pollTimer = null;
    ringer.stop();
    dialNotes.clear();
    if (stopWave) stopWave();
    stopWave = null;
    if (client) {
      // Fire and forget: the page may be unloading, and there is nothing to wait for.
      client.disconnect().catch(() => {});
      client = null;
    }
    if (audioCtx) audioCtx.close().catch(() => {});
    el.remoteAudio.srcObject = null;
    audioCtx = remoteSource = remoteAnalyser = micSource = micAnalyser = null;
  }

  function hangUp(reason) {
    if (ended) return;
    ended = true;
    const secs = startedAt ? Math.floor((Date.now() - startedAt) / 1000) : 0;
    teardown();

    el.phoneScreen.classList.remove("is-live");
    el.endedEyebrow.textContent = secs ? `Call Ended · ${clock(secs)}` : "Call Ended";
    el.endedTitle.textContent = "Auto Fix Auto Electricians";
    el.endedSub.textContent = reason || "";
    el.dialStatus.hidden = true;
    el.dialStatus.classList.remove("is-done");
    show("ended");

    // The card comes back so the next visitor - or the same one, with a different reply -
    // can send another. A handset does not offer to redial, and neither does this.
    returnTimer = setTimeout(() => {
      lockCard(false);
      resetCallScreen();
      show("idle");
    }, reason ? 4000 : 2400);
  }

  function resetCallScreen() {
    el.liveDisc.classList.remove("is-connected", "is-agent", "is-caller");
    el.callState.hidden = false;
    el.callState.textContent = "connecting…";
    el.speakingLabel.textContent = "connecting";
    el.callTimer.hidden = true;
    el.callTimer.textContent = "00:00";
    el.islandTimer.textContent = "00:00";
    el.limitNote.hidden = true;
    el.muteButton.setAttribute("aria-pressed", "false");
    el.muteLabel.textContent = "mute";
  }

  /* ── wiring ─────────────────────────────────────────────────────── */

  el.dialWeb.addEventListener("click", dialWeb);
  el.dialPhone.addEventListener("click", dialPhone);
  el.answerButton.addEventListener("click", answer);
  el.declineButton.addEventListener("click", decline);
  el.endButton.addEventListener("click", () => hangUp(null));

  el.muteButton.addEventListener("click", () => {
    if (!client) return;
    const wasEnabled = client.isMicEnabled;
    client.enableMic(!wasEnabled);
    el.muteButton.setAttribute("aria-pressed", String(wasEnabled));
    el.muteLabel.textContent = wasEnabled ? "unmute" : "mute";
  });

  // Typing in the box is choosing your own words, so no chip is the right answer any
  // more. Leaving one lit would claim the assistant will say something it will not.
  el.briefName.addEventListener("input", reflectName);

  el.replyText.addEventListener("input", () => {
    const typed = el.replyText.textContent.trim();
    const chosen = (situation.replies || []).find((r) => r.text && r.text === typed);
    press(el.replyChips, ".reply-chip", chosen ? chosen.id : null);
  });

  el.gateForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    el.gateError.hidden = true;
    // The gate IS a mint: /api/session is what checks the passcode, and the code is kept
    // alongside the token so a call dialled an hour from now can mint again without asking.
    passcode = el.passcode.value;
    const minted = await sessionToken.get();
    if (minted.ok) return show("idle");

    passcode = "";
    fail(el.gateError, minted.status === 401
      ? "That passcode is not right."
      : "Could not reach the demo server. Try again.");
    el.passcode.select();
  });

  window.addEventListener("beforeunload", teardown);

  /* ── start ──────────────────────────────────────────────────────── */

  (async () => {
    renderCard();
    renderPitch(el.outboundPitch, PITCH, el.outboundPitchTitle);
    try {
      config = await (await fetch("/api/config")).json();
    } catch (_) {
      fail(el.dialError, "Could not reach the demo server.");
    }
    // Hidden rather than disabled where a real phone cannot be rung: a row that 501s is
    // worse than a row that is not there.
    el.phoneMode.hidden = !(config.outbound && config.outbound.phone);

    if (config.passcodeRequired) {
      show("gate");
      el.passcode.focus();
    } else {
      // No gate, so there is nothing to ask for and nothing to mint yet: the token is
      // minted when a call is dialled.
      show("idle");
    }
  })();
})();
